'use client';

import type { Table } from '@tanstack/react-table';
import { Loader, X } from 'lucide-react';
import { AnimatePresence, motion } from 'motion/react';
import * as React from 'react';
import * as ReactDOM from 'react-dom';

import { cn } from "@/lib/utils";
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { Separator } from '@/components/ui/separator';
import { Button } from '@/components/ui/button';

interface DataTableActionBarProps<TData> extends React.ComponentProps<typeof motion.div> {
  table: Table<TData>;
  visible?: boolean;
  container?: Element | DocumentFragment | null;
}

function DataTableActionBar<TData>({
  table,
  visible: visibleProp,
  container: containerProp,
  children,
  className,
  ...props
}: DataTableActionBarProps<TData>) {
  const [mounted, setMounted] = React.useState(false);

  React.useLayoutEffect(() => {
    setMounted(true);
  }, []);

  React.useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        table.toggleAllRowsSelected(false);
      }
    }

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [table]);

  const container = containerProp ?? (mounted ? globalThis.document?.body : null);

  if (!container) return null;

  const visible = visibleProp ?? table.getFilteredSelectedRowModel().rows.length > 0;

  return ReactDOM.createPortal(
    <AnimatePresence>
      {visible && (
        <motion.div
          role="toolbar"
          aria-orientation="horizontal"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 20 }}
          transition={{ duration: 0.2, ease: 'easeInOut' }}
          className={cn(
            'bg-background text-foreground fixed inset-x-3 z-50 mx-auto flex w-auto flex-wrap items-center justify-center gap-2 rounded-lg border p-2 shadow-lg',
            'bottom-[calc(max(env(safe-area-inset-bottom),1rem)+4rem)] sm:inset-x-0 sm:bottom-24 sm:w-fit sm:rounded-md sm:shadow-sm',
            className,
          )}
          {...props}
        >
          {children}
        </motion.div>
      )}
    </AnimatePresence>,
    container,
  );
}

interface DataTableActionBarActionProps extends React.ComponentProps<typeof Button> {
  tooltip?: string;
  isPending?: boolean;
}

const DataTableActionBarAction = React.forwardRef<
  HTMLButtonElement,
  DataTableActionBarActionProps
>(function DataTableActionBarAction(
  {
    size = 'sm',
    variant = 'outline',
    tooltip,
    isPending,
    disabled,
    className,
    children,
    ...props
  },
  ref,
) {
  const trigger = (
    <Button
      ref={ref}
      variant={variant}
      size={size}
      className={cn(
        'gap-1.5 [&>svg]:size-3.5',
        // Ensure proper contrast for all variants
        variant === 'outline' && 'bg-background hover:bg-accent hover:text-accent-foreground',
        variant === 'destructive' && 'bg-destructive text-destructive-foreground hover:bg-destructive/90',
        variant === 'secondary' && 'bg-secondary text-secondary-foreground hover:bg-secondary/80',
        size === 'icon' ? 'size-9 sm:size-7' : 'h-9 sm:h-7',
        className,
      )}
      disabled={disabled || isPending}
      {...props}
    >
      {isPending ? <Loader className="animate-spin" /> : children}
    </Button>
  );

  if (!tooltip) return trigger;

  return (
    <Tooltip>
      <TooltipTrigger asChild>{trigger}</TooltipTrigger>
      <TooltipContent
        sideOffset={6}
        className="bg-accent text-foreground border font-semibold [&>span]:hidden"
      >
        <p>{tooltip}</p>
      </TooltipContent>
    </Tooltip>
  );
});

interface DataTableActionBarSelectionProps<TData> {
  table: Table<TData>;
}

function DataTableActionBarSelection<TData>({ table }: DataTableActionBarSelectionProps<TData>) {
  const onClearSelection = React.useCallback(() => {
    table.toggleAllRowsSelected(false);
  }, [table]);

  return (
    <div className="flex h-9 items-center rounded-md border pl-2.5 pr-1 sm:h-7">
      <span className="whitespace-nowrap text-xs">
        {table.getFilteredSelectedRowModel().rows.length} selected
      </span>
      <Separator orientation="vertical" className="ml-2 mr-1 data-[orientation=vertical]:h-4" />
      <Tooltip>
        <TooltipTrigger asChild>
          <Button variant="ghost" size="icon" className="size-5" onClick={onClearSelection}>
            <X className="size-3.5" />
          </Button>
        </TooltipTrigger>
        <TooltipContent
          sideOffset={10}
          className="bg-accent text-foreground flex items-center gap-2 border px-2 py-1 font-semibold [&>span]:hidden"
        >
          <p>Clear selection</p>
          <kbd className="border-border/60 bg-muted/50 text-muted-foreground/70 inline-flex h-5 select-none items-center rounded border px-1.5 font-mono text-[10px] font-medium">
            <abbr title="Escape" className="no-underline">
              Esc
            </abbr>
          </kbd>
        </TooltipContent>
      </Tooltip>
    </div>
  );
}

export { DataTableActionBar, DataTableActionBarAction, DataTableActionBarSelection };
